import React, { useState } from 'react'
import {
  Box,
  ButtonBase,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  MenuItem,
  Popover,
  Select,
  Tooltip,
  Typography,
} from '@mui/material'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import CloseIcon from '@mui/icons-material/Close'
import { isAnyAnswer, type Condition } from './builderModel'
import { containerEdge } from './surface'
import AddSelectionButton from '../tree/AddSelectionButton'
import type { Category } from './useBuilderData'

// Small shared pieces of the construct builder.

// IsNotSelect is the "is" / "is not" choice of a condition. "is not" means
// the respondent has none of the chosen codes (Carbon "!").
export const IsNotSelect: React.FC<{
  not: boolean
  onChange: (not: boolean) => void
  label: string
}> = ({ not, onChange, label }) => (
  <Select
    size="small"
    value={not ? 'not' : 'is'}
    onChange={e => onChange(e.target.value === 'not')}
    inputProps={{ 'aria-label': label }}
    sx={{ fontSize: 13, minWidth: 84, '& .MuiSelect-select': { py: 0.5 } }}
  >
    <MenuItem value="is">is</MenuItem>
    <MenuItem value="not">is not</MenuItem>
  </Select>
)

// JoinSelect is the "and" / "or" between two conditions of a line: a small
// outlined menu at body2 size, the joint between two cards. "or" starts a
// new or-row (Condition.or); "and" binds tighter, as in Carbon.
export const JoinSelect: React.FC<{
  join: 'and' | 'or'
  onChange: (join: 'and' | 'or') => void
  label: string
}> = ({ join, onChange, label }) => (
  <Select
    size="small"
    value={join}
    onChange={e => onChange(e.target.value === 'or' ? 'or' : 'and')}
    inputProps={{ 'aria-label': label }}
    sx={{
      fontSize: 14,
      fontWeight: 600,
      minWidth: 76,
      bgcolor: 'background.paper',
      color: join === 'or' ? 'info.main' : 'text.primary',
      '& .MuiSelect-select': { py: 0.5, pl: 1.25 },
    }}
  >
    <MenuItem value="and">and</MenuItem>
    <MenuItem value="or">or</MenuItem>
  </Select>
)

// withCodeToggled adds or takes off one code, keeping the variable's own
// order (codes it does not list stay last). The order is only for the eye:
// codesToCarbon sorts when it writes the rule.
const withCodeToggled = (codes: string[], code: string, on: boolean, categories: Category[] = []): string[] => {
  const set = new Set(on ? [...codes, code] : codes.filter(c => c !== code))
  const known = categories.map(c => c.code)
  return [...known.filter(c => set.has(c)), ...[...set].filter(c => !known.includes(c))]
}

// AnswersField is a condition's answers as ONE control, like a multi-select:
// an outlined field holding the answer chips with a ▾ at its right edge.
// A click anywhere in it (a chip included) opens the answer picker, a
// checkbox list of the variable's categories, loaded on open. "any answer"
// has no x (narrow it in the picker; the card's own x removes the
// condition); a specific answer's small x drops just that answer.
const AnswersField: React.FC<{
  variable: string
  codes: string[]
  categories: Category[] | undefined
  labelOf: (code: string) => string
  not: boolean
  onOpen: () => void
  onChange: (codes: string[]) => void
}> = ({ variable, codes, categories, labelOf, not, onOpen, onChange }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  // "Any answer" shows every answer ticked; unticking one narrows it to the
  // rest.
  const shown = isAnyAnswer(codes) ? (categories || []).map(c => c.code) : codes
  const open = (el: HTMLElement) => { onOpen(); setAnchor(el) }
  // "is not" reads as an exclusion: dashed chip outlines, quietly.
  const chipSx = { maxWidth: '100%', ...(not ? { borderStyle: 'dashed' } : {}) }
  return (
    <>
      <ButtonBase
        component="div"
        role="button"
        aria-label={`Answers for ${variable}`}
        aria-haspopup="true"
        aria-expanded={Boolean(anchor)}
        onClick={e => open(e.currentTarget)}
        sx={{
          flex: '1 1 180px',
          minWidth: 0,
          minHeight: 36,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          flexWrap: 'wrap',
          gap: 0.5,
          pl: 0.75,
          pr: 3.5,
          py: 0.5,
          position: 'relative',
          textAlign: 'left',
          border: '1px solid',
          borderColor: containerEdge,
          borderRadius: 1,
          bgcolor: 'background.paper',
          '&:hover': { borderColor: 'text.primary' },
          '&.Mui-focusVisible': { borderColor: 'info.main', boxShadow: theme => `0 0 0 1px ${theme.palette.info.main}` },
        }}
      >
        {isAnyAnswer(codes) ? (
          <Chip size="small" variant="outlined" label="any answer" sx={chipSx} />
        ) : codes.map(code => (
          <Chip
            key={code}
            size="small"
            variant="outlined"
            label={labelOf(code)}
            onDelete={e => {
              e.stopPropagation()
              onChange(codes.filter(c => c !== code))
            }}
            deleteIcon={<CloseIcon aria-label={`Remove ${labelOf(code)}`} />}
            sx={chipSx}
          />
        ))}
        <ArrowDropDownIcon fontSize="small" sx={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', color: 'action.active' }} />
      </ButtonBase>
      <Popover open={Boolean(anchor)} anchorEl={anchor} onClose={() => setAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        <Box role="group" aria-label={`Answers for ${variable}`} sx={{ py: 1, px: 1.5, maxWidth: 360, maxHeight: 320, overflowY: 'auto', display: 'grid' }}>
          {!categories && <Typography variant="caption" color="text.secondary">Loading answers…</Typography>}
          {categories?.length === 0 && (
            <Typography variant="caption" color="text.secondary">This question has no answers to pick. Type its rule instead.</Typography>
          )}
          {categories?.map(c => (
            <FormControlLabel
              key={c.code}
              sx={{ mr: 0 }}
              control={<Checkbox size="small" color="info" checked={shown.includes(c.code)} onChange={e => onChange(withCodeToggled(shown, c.code, e.target.checked, categories))} />}
              label={<Typography variant="body2">{c.label}</Typography>}
            />
          ))}
        </Box>
      </Popover>
    </>
  )
}

// ConditionRow is one condition of a line, as a white card inside the line's
// tinted container: the variable's name, its question below in small grey,
// then "is" / "is not", the answers field (AnswersField) and the remove x.
// Taking the last answer off removes the condition.
export const ConditionRow: React.FC<{
  condition: Condition
  question?: string
  categories: Category[] | undefined
  labelOf: (code: string) => string
  onOpenVariable: () => void
  onChange: (condition: Condition) => void
  onDelete: () => void
}> = ({ condition, question, categories, labelOf, onOpenVariable, onChange, onDelete }) => {
  const { variable, codes, not } = condition
  const setCodes = (next: string[]) => (next.length === 0 ? onDelete() : onChange({ ...condition, codes: next }))
  const setNot = (value: boolean) => {
    const rest: Condition = { ...condition }
    delete rest.not
    onChange(value ? { ...rest, not: true } : rest)
  }
  return (
    <Box
      data-testid={`condition-${variable}`}
      sx={{
        display: 'grid',
        gap: 0.5,
        border: '1px solid',
        borderColor: containerEdge,
        borderRadius: 1.5,
        px: 1.25,
        py: 1,
        bgcolor: 'background.paper',
        minWidth: 0,
      }}
    >
      <Typography variant="body2" color="text.primary" noWrap>{variable}</Typography>
      {question && question !== variable && (
        <Tooltip title={question} placement="bottom-start">
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.3 }}
          >
            {question}
          </Typography>
        </Tooltip>
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, minWidth: 0, mt: 0.25 }}>
        <IsNotSelect not={!!not} onChange={setNot} label={`${variable}: is or is not`} />
        <AnswersField variable={variable} codes={codes} categories={categories} labelOf={labelOf} not={!!not} onOpen={onOpenVariable} onChange={setCodes} />
        <Tooltip title="Remove condition">
          <IconButton size="small" aria-label={`Remove condition on ${variable}`} onClick={onDelete} sx={{ ml: 'auto' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  )
}

// AddTarget is a green + with the words beside it: a place the tree's ticked
// selection can go. Codes and Cross two variables both use it, and it is always green and working: a place the selection
// cannot go is not shown at all (never a grey target). Per-group targets sit
// at the end of their group's card; the new-group target sits below the last
// group.
export const AddTarget: React.FC<{ label: string, text: string, onClick: () => void }> = ({ label, text, onClick }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifySelf: 'start' }}>
    <AddSelectionButton label={label} tooltip={text} onClick={onClick} />
    <Typography variant="body2" color="text.secondary" aria-hidden onClick={onClick} sx={{ cursor: 'pointer' }}>
      {text}
    </Typography>
  </Box>
)

// TargetHint is one short neutral line under the targets: why none are
// shown for what is ticked, or why an add did nothing.
export const TargetHint: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="body2" color="text.secondary" role="status" sx={{ fontStyle: 'italic' }}>
    {children}
  </Typography>
)
